import React from 'react'
import mParticleSdk from '@mparticle/web-sdk'
import type * as MParticle from '@mparticle/web-sdk'
import optimizelyKit from '@mparticle/web-optimizely-kit'
import { useConsentSyncing } from './useConsentSyncing'
import { getCookieValue } from 'utils/getCookieValue'

export type MParticleInstance = typeof MParticle

interface InternalAnalyticsApi {
  mParticle?: MParticleInstance
  consentSynced: boolean
}
interface InternalAnalyticsProviderProps {
  children: React.ReactNode
  mParticleApiKey: string
  isMParticleDevMode: boolean
  isMParticleEnabled: boolean
  isOrgOptimizelyEnabled?: boolean
}

/**
 * Context providing access to mParticle analytics instance and consent status.
 * Used internally by useInternalAnalytics hook.
 */
export const InternalAnalyticsContext = React.createContext<InternalAnalyticsApi>({
  consentSynced: false,
})

/**
 * Hook to access mParticle analytics instance and consent synchronization status.
 */
export const useInternalAnalytics = (): InternalAnalyticsApi =>
  React.useContext(InternalAnalyticsContext)

/**
 * Provider component that initializes and manages mParticle analytics integration.
 *
 * This component:
 * - Initializes mParticle SDK with the provided API key and configuration
 * - Manages mParticle user state and consent synchronization
 * - Creates a named instance ('classyInstance') to avoid conflicts
 *   with NPO's mParticle implementations
 * - Provides mParticle instance and consent status to child components via Context
 *
 * @component
 * @param {Object} props
 * @param {string} props.appEnv - Application environment ('prod', 'staging', etc.)
 * @param {string} props.mParticleApiKey - mParticle API key for initialization
 * @param {boolean} [props.isOrgOptimizelyEnabled] - Whether org has their own Optimizely setup.
 * If true, mParticle optimizely kit is NOT registered to avoid conflicts.
 * @param {React.ReactNode} props.children - Child components that will have
 * access to the analytics context
 *
 * @example
 * ```tsx
 * <InternalAnalyticsProvider
 *   appEnv="prod"
 *   mParticleApiKey="12345"
 *   isOrgOptimizelyEnabled={false}
 * >
 *   <App />
 * </InternalAnalyticsProvider>
 * ```
 */
export const InternalAnalyticsProvider: React.FC<InternalAnalyticsProviderProps> = ({
  children,
  mParticleApiKey,
  isMParticleDevMode,
  isMParticleEnabled,
  isOrgOptimizelyEnabled = false,
}) => {
  const [mParticleUser, setMParticleUser] = React.useState<MParticle.User>()
  const [mParticle, setMParticle] = React.useState<MParticleInstance>()
  const consentSynced = useConsentSyncing(mParticle, mParticleUser)

  React.useEffect(() => {
    const init = async () => {
      const mParticleConfig: MParticle.MPConfiguration = {
        appName: 'whammy-web',
        dataPlan: { planId: 'web_client_data_plan' },
        identifyRequest: {
          userIdentities: {
            other3: getCookieValue('pgdid'),
          },
        },
        identityCallback() {
          /**
           * Discovered a bug in mParticle's SDK where `result.getUser().isLoggedIn()`
           * returns `undefined` when the user is logged in, so we're working
           * around that by using the user from `sdk.Identity.getCurrentUser()`
           * (which does have the correct logged-in state)
           */
          setMParticleUser(mParticleSdk.getInstance('classyInstance').Identity.getCurrentUser())
        },
        isDevelopmentMode: isMParticleDevMode,
        logLevel: isMParticleDevMode ? 'verbose' : 'none',
      }

      // * Register the optimizely kit with our mParticle instance
      // * Only register if organization doesn't have their own Optimizely setup
      if (!isOrgOptimizelyEnabled) {
        optimizelyKit.register(mParticleConfig)
      }

      // ! We need to name the mParticle instance so that there are
      // ! no colisions with an NPO's possible usage of mParticle in a GTM or Tealium tag.
      mParticleSdk.init(mParticleApiKey, mParticleConfig, 'classyInstance')
      setMParticle(mParticleSdk?.getInstance('classyInstance') as MParticleInstance)
    }
    if (!mParticle && isMParticleEnabled) {
      init()
    }
  }, [isMParticleDevMode, isMParticleEnabled, isOrgOptimizelyEnabled, mParticleApiKey, mParticle])

  return (
    <InternalAnalyticsContext.Provider value={{ consentSynced, mParticle }}>
      {children}
    </InternalAnalyticsContext.Provider>
  )
}
